import { useEffect, useRef } from 'react'

export interface UseDebouncedOptions {
  /**
   * Опция для страниц, где размонтирование = «ушёл со страницы» (p3-sout-workplaces §4/§13,
   * Opus-ревью Волны 4, MAJOR-1): если ЕСТЬ несработавший таймер на момент размонтирования —
   * запустить отложенный колбэк НЕМЕДЛЕННО (fire-and-forget: дожидаться некому, компонент уже
   * ушёл), а не молча его отбросить. Без этого браузерный «Назад»/закрытие вкладки/переход по
   * ссылке внутри debounce-окна (600мс) тихо теряют последнюю правку — вопреки «изменения
   * сохраняются автоматически» (§4/§13). По умолчанию выключено (`false`) — визард анкеты
   * (`sout-wizard.tsx`) явно ждёт `flush()` перед `submit()`, доп. поведение при unmount ему не
   * нужно и могло бы неожиданно послать лишний PATCH при обычной навигации между шагами анкеты.
   */
  flushOnUnmount?: boolean
}

/**
 * Дебаунс автосохранения визарда СОУТ: `schedule(fn)` запускает последний переданный колбэк через
 * `delay` мс (предыдущий отменяется); `flush` выполняет отложенный немедленно (или дожидается уже
 * стартовавшего по таймеру запроса) и возвращает его промис (`Promise<void>`), чтобы подача могла
 * дождаться долёта последнего PATCH. `cancel` отменяет ещё не сработавший таймер БЕЗ выполнения
 * колбэка (p3-sout-workplaces §13, Opus-ревью Волны 6) — для страниц, которые сами берут на себя
 * авторитетное сохранение перед уходом (см. `order-sout-workplace.tsx`, `goBack`/`saveNow`) и не
 * хотят, чтобы отложенный автосейв ЕЩЁ РАЗ продублировал тот же запрос через `flushOnUnmount`.
 * Очищается при размонтировании (либо flush'ится — см. `flushOnUnmount`).
 *
 * `inFlight` нужен отдельно от `timer`: если таймер сработал САМ (пользователь не звал flush),
 * `run()` синхронно обнуляет `timer.current` ещё до того, как асинхронный PATCH долетит до сервера.
 * Без `inFlight` `flush()`, вызванный в этот промежуток, видел бы «нечего ждать» и резолвился
 * немедленно — submit() уходил бы раньше последнего сохранения (ложный 422 на актуальные данные).
 */
export function useDebounced(
  delay = 600,
  options?: UseDebouncedOptions,
): {
  schedule: (fn: () => void | Promise<void>) => void
  flush: () => Promise<void>
  /** Отменяет ещё не сработавший таймер БЕЗ выполнения колбэка (§13, см. комментарий выше). */
  cancel: () => void
} {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<(() => void | Promise<void>) | null>(null)
  const inFlight = useRef<Promise<void> | null>(null)
  // Читаем актуальный флаг в cleanup-эффекте с deps=[] через ref (а не напрямую из замыкания) —
  // cleanup ставится один раз при монтировании и иначе видел бы только значение первого рендера.
  const flushOnUnmountRef = useRef(options?.flushOnUnmount ?? false)
  flushOnUnmountRef.current = options?.flushOnUnmount ?? false

  const run = (): Promise<void> => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    const fn = pending.current
    pending.current = null
    const result = fn ? Promise.resolve(fn()) : Promise.resolve()
    inFlight.current = result
    void result.finally(() => {
      if (inFlight.current === result) inFlight.current = null
    })
    return result
  }

  const schedule = (fn: () => void | Promise<void>) => {
    pending.current = fn
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(run, delay)
  }

  const flush = (): Promise<void> => {
    if (timer.current) return run()
    return inFlight.current ?? Promise.resolve()
  }

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    pending.current = null
  }

  useEffect(
    () => () => {
      if (!timer.current) return
      if (flushOnUnmountRef.current) {
        // Компонент уходит (навигация/закрытие вкладки) с несработавшим таймером — запускаем
        // отложенный PATCH СЕЙЧАС, а не тихо его теряем (MAJOR-1). Не await'им результат: ждать
        // уже некому, важно лишь, что запрос фактически ушёл в сеть (закрытие вкладки его не
        // отменит — fetch/XHR уже стартовал синхронно внутри `run()` до первого `await`).
        void run()
        return
      }
      clearTimeout(timer.current)
    },
    [],
  )

  return { schedule, flush, cancel }
}
